import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { usePopup } from '../contexts/PopupContext';
import { useTheme } from '../contexts/ThemeContext';
import FullscreenSafeModal from './FullscreenSafeModal';

export function ConfirmationDialog() {
  const { confirmation, handleConfirm, handleCancel } = usePopup();
  const { theme, isDarkMode } = useTheme();
  const styles = useMemo(() => createStyles(theme, isDarkMode), [theme, isDarkMode]);

  if (!confirmation.visible) return null;

  const isDanger = confirmation.confirmVariant === 'danger';
  const iconName = confirmation.iconName || (isDanger ? 'alert-circle-outline' : 'help-circle-outline');
  const iconColor = isDanger ? theme.status.error : theme.brand.accent;

  return (
    <FullscreenSafeModal
      transparent
      visible={confirmation.visible}
      animationType="fade"
      onRequestClose={handleCancel}
    >
      <View style={styles.overlay}>
        <Pressable style={styles.overlayDismiss} onPress={handleCancel} accessibilityLabel="Dismiss" />
        <View style={styles.card}>
          <View style={styles.content}>
            <View style={styles.iconWrap}>
              <Ionicons name={iconName} size={36} color={iconColor} />
            </View>
            <Text style={styles.title}>{confirmation.title}</Text>
            <Text style={styles.message}>{confirmation.message}</Text>
          </View>
          <View style={styles.buttonRow}>
            <TouchableOpacity style={styles.cancelBtn} onPress={handleCancel}>
              <Text style={styles.cancelBtnText}>{confirmation.cancelLabel}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.confirmBtn, isDanger ? styles.confirmBtnDanger : styles.confirmBtnPrimary]}
              onPress={handleConfirm}
            >
              <Text style={styles.confirmBtnText}>{confirmation.confirmLabel}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </FullscreenSafeModal>
  );
}

function createStyles(theme, isDarkMode) {
  const glassBg = isDarkMode ? 'rgba(6,9,22,0.94)' : theme.background.elevated;
  const webGlass =
    Platform.OS === 'web'
      ? {
          backdropFilter: 'blur(22px) saturate(165%)',
          WebkitBackdropFilter: 'blur(22px) saturate(165%)',
          boxShadow: isDarkMode ? '0 16px 48px rgba(0,0,0,0.55)' : '0 16px 48px rgba(0,0,0,0.12)',
        }
      : {};

  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: theme.background.overlay,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      ...(Platform.OS === 'web'
        ? {
            // absoluteFill so this also works when portaled into map fullscreen stage
            ...StyleSheet.absoluteFillObject,
            width: '100%',
            height: '100%',
          }
        : {}),
    },
    overlayDismiss: {
      ...StyleSheet.absoluteFillObject,
    },
    card: {
      width: '100%',
      maxWidth: 400,
      backgroundColor: glassBg,
      borderRadius: 14,
      padding: 24,
      borderWidth: 1,
      borderColor: theme.ui.border,
      alignSelf: 'center',
      zIndex: 1,
      ...webGlass,
    },
    content: {
      width: '100%',
      alignItems: 'center',
    },
    iconWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 12,
    },
    title: {
      width: '100%',
      fontSize: 18,
      fontWeight: '800',
      color: theme.text.primary,
      textAlign: 'center',
      marginBottom: 8,
    },
    message: {
      width: '100%',
      fontSize: 14,
      lineHeight: 20,
      color: theme.text.secondary,
      textAlign: 'center',
      marginBottom: 20,
    },
    buttonRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'stretch',
      gap: 10,
      width: '100%',
    },
    cancelBtn: {
      flex: 1,
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 10,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : theme.background.secondary,
      borderWidth: 1,
      borderColor: theme.ui.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cancelBtnText: {
      color: theme.text.primary,
      fontWeight: '700',
      fontSize: 15,
      textAlign: 'center',
    },
    confirmBtn: {
      flex: 1,
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    confirmBtnPrimary: {
      backgroundColor: theme.brand.accent,
    },
    confirmBtnDanger: {
      backgroundColor: theme.status.error,
    },
    confirmBtnText: {
      color: '#fff',
      fontWeight: '800',
      fontSize: 15,
      textAlign: 'center',
    },
  });
}
